import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  generatePDF,
  getChecklistAnswered,
  validateChecklist,
} from "../../api/checklistApi";
import {
  ChecklistData,
  ObservationResponse,
  RespostaFoto,
} from "../../types/checklistData";
import { ThemeProvider } from "@emotion/react";
import {
  Box,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
  createTheme,
  Toolbar,
  CircularProgress,
  Button,
  AppBar,
  Modal,
  Fade,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import {
  ArrowBack,
  ArrowDownward,
  Block,
  CameraAlt,
  ChangeHistory,
  Close,
  Done,
  DoneAll,
  ErrorOutline,
  PictureAsPdf,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { AxiosError } from "axios";
import { formatPhone } from "../../services/formatDocument";
import { formatCurrency, formatCurrency2 } from "../../services/formatCurrency";

const VisualizarChecklist: React.FC = () => {
  const token = localStorage.getItem("token");
  const { id_checklist_respondido } = useParams<{
    id_checklist_respondido: string;
  }>();
  const navigate = useNavigate();

  const [checklistData, setChecklistData] = useState<ChecklistData | null>(
    null,
  );
  const [selectedIcons, setSelectedIcons] = useState<{
    [key: number]: string | null;
  }>({});

  const [selectedDescriptions, setSelectedDescriptions] = useState<{
    [key: number]: string | null;
  }>({});

  const [isExpanded, setIsExpanded] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [openValidationModal, setOpenValidationModal] = useState(false);
  const [commercialCondition, setCommercialCondition] = useState("");
  const [purchaseValue, setPurchaseValue] = useState("");
  const [showValidationFields, setShowValidationFields] = useState(false);
  const [rawPurchaseValue, setRawPurchaseValue] = useState("");
  const [selectedResponsePhoto, setSelectedResponsePhoto] = useState<
    string | null
  >(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);

  const defaultTheme = createTheme({
    typography: {
      fontFamily: "Poppins, sans-serif",
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    setRawPurchaseValue(rawValue);
    const formatted = formatCurrency(rawValue);
    setPurchaseValue(formatted);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }
        if (!id_checklist_respondido) {
          throw new Error("Parâmetros de rota ausentes");
        }
        const data = await getChecklistAnswered(token, id_checklist_respondido);
        setChecklistData(data);

        const updatedIcons: { [key: number]: string | null } = {};
        data.resposta.forEach((item: any) => {
          const pergunta = data.checklist.grupo_perguntas
            .flatMap((grupo: any) => grupo.perguntas)
            .find(
              (pergunta: any) => pergunta.id === item.id_checklist_pergunta,
            );

          if (pergunta) {
            const resposta = pergunta.respostas.find(
              (resposta: any) =>
                resposta.valor_checklist_respostas ===
                item.valor_checklist_respondido_resposta,
            );

            if (resposta) {
              updatedIcons[item.id_checklist_pergunta] =
                item.valor_checklist_respondido_resposta;
            }
          }
        });

        setSelectedIcons(updatedIcons);

        const updatedDescriptions: { [key: number]: string | null } = {};

        data.resposta.forEach((item: any) => {
          const pergunta = data.checklist.grupo_perguntas
            .flatMap((grupo: any) => grupo.perguntas)
            .find(
              (pergunta: any) => pergunta.id === item.id_checklist_pergunta,
            );

          if (pergunta) {
            if (
              item.valor_checklist_respondido_resposta !== "S" &&
              item.valor_checklist_respondido_resposta !== "N" &&
              item.valor_checklist_respondido_resposta !== "NA"
            ) {
              updatedDescriptions[pergunta.id] =
                item.valor_checklist_respondido_resposta;
            }
          }
        });

        setSelectedDescriptions(updatedDescriptions);
      } catch (error) {
        if (error instanceof AxiosError && error.response) {
          const errorMessage = error.response.data.error;
          toast.error(errorMessage);
        } else {
          toast.error("Erro ao buscar checklist");
        }
      }
    };

    fetchData();
  }, [id_checklist_respondido]);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const isIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const handleGeneratePDF = async () => {
    try {
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }
      setLoading(true);

      if (isIOS()) {
        const result = await generatePDF(
          token,
          Number(id_checklist_respondido),
        );
        const url: string = result.pdf;
        setPdfUrl(url);
        setPdfGenerated(true);
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      handleOpenModal();
      const pdfUrl = await generatePDF(token, Number(id_checklist_respondido));
      setPdfUrl(pdfUrl.pdf);
      setPdfGenerated(true);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      toast.error("Erro ao gerar PDF");
      setOpenModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateChecklist = () => {
    if (checklistData?.checklist.checklist_valida_comercial === true) {
      setOpenValidationModal(true);
      setShowValidationFields(false);
    } else {
      handleConfirmValidation(true);
    }
  };

  const formatCurrencyToNumber = (currencyString: string): number => {
    const cleaned = currencyString.replace(/[^\d,-]/g, "").replace(".", "");
    const numericString = cleaned.replace(",", ".");
    return parseFloat(numericString);
  };

  const handleConfirmValidation = async (validated: boolean) => {
    try {
      if (!token || !id_checklist_respondido) {
        throw new Error("Dados incompletos para validação");
      }

      setLoading(true);

      const numericValue = purchaseValue
        ? formatCurrencyToNumber(purchaseValue)
        : null;

      const validationData = {
        id_checklist_respondido: Number(id_checklist_respondido),
        validado: validated,
        condicao_checklist_respondido:
          validated &&
          checklistData?.checklist.checklist_valida_comercial === true
            ? commercialCondition
            : "",
        valor_checklist_respondido:
          validated &&
          checklistData?.checklist.checklist_valida_comercial === true
            ? numericValue
            : null,
      };

      await validateChecklist(token, validationData);

      toast.success(
        validated
          ? "Checklist validado com sucesso!"
          : "Checklist Rejeitado com sucesso!",
      );

      const updatedData = await getChecklistAnswered(
        token,
        id_checklist_respondido,
      );
      setChecklistData(updatedData);
      setOpenValidationModal(false);
      setCommercialCondition("");
      setPurchaseValue("");
    } catch (error) {
      console.error("Erro ao validar checklist:", error);
      const errorMessage =
        error instanceof AxiosError && error.response?.data?.error
          ? error.response.data.error
          : "Erro ao validar checklist";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderPhotoGallery = () => {
    if (
      !checklistData ||
      !checklistData.fotos ||
      checklistData.fotos.length === 0
    ) {
      return null;
    }

    return (
      <Box mt={6}>
        <Typography
          variant="h5"
          align="center"
          gutterBottom
          fontWeight={"bold"}
          mb={3}
        >
          GALERIA DE FOTOS
        </Typography>

        <Grid container spacing={2}>
          {checklistData.fotos.map((foto) => (
            <Grid item xs={12} sm={6} md={4} key={foto.id}>
              <Card>
                <CardContent sx={{ p: 0 }}>
                  <img
                    src={`${process.env.REACT_APP_API_DOMAIN}/storage/${foto.foto_checklist_respondido_foto}`}
                    alt={`Foto do checklist ${foto.id}`}
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "300px",
                      objectFit: "contain",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setSelectedPhoto(
                        `${process.env.REACT_APP_API_DOMAIN}/storage/${foto.foto_checklist_respondido_foto}`,
                      )
                    }
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Modal
          open={!!selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
          aria-labelledby="image-modal"
          aria-describedby="image-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: "800px",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 1,
              outline: "none",
            }}
          >
            {selectedPhoto && (
              <img
                src={selectedPhoto}
                alt="Ampliada"
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "80vh",
                }}
              />
            )}
            <Box textAlign="center" mt={1}>
              <Button
                variant="contained"
                onClick={() => setSelectedPhoto(null)}
                sx={{ backgroundColor: "black" }}
              >
                Fechar
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    );
  };

  const getResponsePhotos = (perguntaId: number) => {
    if (!checklistData?.resposta) return [];

    const respostas = checklistData.resposta.filter(
      (r) => r.id_checklist_pergunta === perguntaId,
    );

    const fotos: RespostaFoto[] = [];

    respostas.forEach((resposta) => {
      if (resposta.fotos && resposta.fotos.length > 0) {
        fotos.push(...resposta.fotos);
      }
    });

    return fotos;
  };

  if (!checklistData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress sx={{ color: "#000" }} />
        <Typography variant="h5" sx={{ mt: 2 }}>
          Carregando Checklist
        </Typography>
      </Box>
    );
  }

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const renderCabDetails = () => {
    if (!checklistData || !checklistData.cab) return null;

    const keyMapping: { [key: string]: string } = {
      chassi_veiculo: "V.I.N",
      data_recebimento_veiculo: "Data de Recebimento",
      km_veiculo: "Km Inicial",
      localidade_veiculo: "Localidade",
      codigo_radio_veiculo: "Código do Rádio",
      ano_veiculo: "Ano do Veículo",
      data_armazenagem_veiculo: "Data de Armazenagem",
      km_final_veiculo: "Km Final",
      descricao_cor: "Cor",
      descricao_modelo: "Modelo",
      descricao_ano_modelo: "Ano do Modelo",
      descricao_local: "Local de Armazenagem",
      nome_cliente: "Nome do Cliente",
      telefone_contato_cliente: "Telefone",
      permite_fotos_veiculo: "Permite uso das Fotos",
      empresa_nome: "Empresa",
      usuario_responsavel: "Usuário Responsável",
      placa_veiculo: "Placa",
    };

    const initialVehicleDetails = {
      chassi_veiculo: checklistData.cab.chassi_veiculo,
      km_veiculo: checklistData.cab.km_veiculo,
      descricao_local: checklistData.cab.descricao_local,
      descricao_cor: checklistData.cab.descricao_cor,
    };

    return (
      <>
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Paper
              sx={{
                border: 1,
                height: isExpanded ? "auto" : "auto",
                cursor: "pointer",
              }}
              onClick={handleExpandClick}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  position: "relative",
                  width: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  bgcolor={"black"}
                  component={"span"}
                  color={"white"}
                  textAlign={"center"}
                  sx={{ flex: 1 }}
                >
                  Informações
                </Typography>
                <IconButton
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: "5px",
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease-in-out",
                    mt: -2.6,
                  }}
                  onClick={handleExpandClick}
                >
                  <ArrowDownward sx={{ color: "white" }} />
                </IconButton>
              </Box>

              <Typography
                variant="body1"
                textAlign={"center"}
                component={"span"}
                mt={1.7}
                sx={{ padding: 1 }}
              >
                {isExpanded ? (
                  <>
                    <Grid item={true} container spacing={2}>
                      {Object.keys(checklistData.cab)
                        .filter(
                          (key) =>
                            !key.includes("id") &&
                            !key.includes("entrega_veiculo") &&
                            !key.includes("tem_checklist_agrupado") &&
                            !key.includes("somente_atendimento_veiculo") &&
                            !key.includes("recusa_veiculo") &&
                            !key.includes("acoes") &&
                            !key.includes("grupo") &&
                            !key.includes("transferencia_veiculo") &&
                            !key.includes("nome_cliente") &&
                            checklistData.cab[
                              key as keyof typeof checklistData.cab
                            ] !== null &&
                            key !== "cliente",
                        )
                        .map((key) => {
                          let value = JSON.stringify(
                            checklistData.cab[
                              key as keyof typeof checklistData.cab
                            ],
                          ).replace(/"/g, "");
                          if (key === "data_recebimento_veiculo") {
                            const date = new Date(value);
                            if (!isNaN(date.getTime())) {
                              value = date.toLocaleString("pt-BR");
                            }
                          } else if (key === "data_armazenagem_veiculo") {
                            const date = new Date(value);
                            if (!isNaN(date.getTime())) {
                              value = date.toLocaleDateString("pt-BR");
                            }
                          } else if (key === "permite_fotos_veiculo") {
                            value = value ? "Sim" : "Não";
                          }

                          return (
                            <Grid
                              item={true}
                              xs={12}
                              sm={6}
                              md={4}
                              lg={4}
                              sx={{
                                borderRight: "1px solid #ccc",
                                paddingBottom: 1,
                              }}
                              key={key}
                            >
                              <Typography fontWeight={"bold"} fontSize={16}>
                                {keyMapping[key] || key}
                              </Typography>

                              <Typography>{value}</Typography>
                            </Grid>
                          );
                        })}
                      {checklistData.cab.cliente && (
                        <Grid
                          item={true}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={4}
                          sx={{
                            borderRight: "1px solid #ccc",
                            paddingBottom: 1,
                          }}
                        >
                          <Typography fontWeight={"bold"} fontSize={16}>
                            {keyMapping["nome_cliente"]}
                          </Typography>
                          <Typography>
                            {checklistData.cab.cliente.nome_cliente}
                          </Typography>{" "}
                          <Typography fontWeight={"bold"} fontSize={16} mt={2}>
                            {keyMapping["telefone_contato_cliente"]}
                          </Typography>
                          <Typography>
                            {formatPhone(
                              checklistData.cab.cliente
                                .telefone_contato_cliente,
                            )}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid
                      item={true}
                      container
                      spacing={2}
                      justifyContent="center"
                    >
                      <Grid
                        item={true}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={4}
                        sx={{ borderRight: "1px solid #ccc", paddingBottom: 1 }}
                      >
                        <Typography fontWeight={"bold"} fontSize={16}>
                          V.I.N
                        </Typography>
                        <Typography>
                          {initialVehicleDetails.chassi_veiculo}
                        </Typography>
                      </Grid>
                      <Grid
                        item={true}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={4}
                        sx={{ borderRight: "1px solid #ccc", paddingBottom: 1 }}
                      >
                        <Typography fontWeight={"bold"} fontSize={16}>
                          Km Inicial
                        </Typography>
                        <Typography>
                          {initialVehicleDetails.km_veiculo}
                        </Typography>
                      </Grid>
                      <Grid
                        item={true}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={4}
                        sx={{ paddingBottom: 1 }}
                      >
                        <Typography fontWeight={"bold"} fontSize={16}>
                          Local de Armazenagem
                        </Typography>
                        <Typography>
                          {initialVehicleDetails.descricao_local}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container
        maxWidth="lg"
        sx={{
          mt: { xs: 8, sm: 15 },
          mb: { xs: 20, sm: 30 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Toolbar sx={{ mb: 1, mt: -2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              "&:hover": {
                cursor: "pointer",
                color: "red",
              },
            }}
            onClick={() => navigate("/veiculos")}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="voltar"
              sx={{ color: "inherit" }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h6"
              component="div"
              sx={{ color: "inherit", mt: 0.2 }}
            >
              Voltar
            </Typography>
          </Box>
        </Toolbar>
        {checklistData && (
          <>
            <Typography
              variant="h3"
              fontWeight="bold"
              textAlign="center"
              mb={8}
              sx={{
                fontSize: { xs: "1.8rem", sm: "2.4rem", md: "3rem" },
              }}
            >
              {checklistData.checklist.descricao_checklist}
            </Typography>
            <Grid>{renderCabDetails()}</Grid>
            {checklistData.checklist.legenda && (
              <Box mt={5}>
                <Typography
                  variant="h5"
                  bgcolor="black"
                  color="white"
                  textAlign="center"
                  height="auto"
                  mb={0}
                  sx={{ paddingTop: "10px" }}
                >
                  {checklistData.checklist.legenda}
                </Typography>
              </Box>
            )}
            {checklistData.cab.valor_checklist_respondido &&
              checklistData.cab.condicao_checklist_respondido && (
                <Grid container alignItems="center" mt={2}>
                  <Grid item xs={12}>
                    <Paper sx={{ border: 1, height: "auto" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          bgcolor={"black"}
                          color={"white"}
                          textAlign={"center"}
                          sx={{ width: "100%", py: 1 }}
                        >
                          Dados Comerciais
                        </Typography>
                      </Box>

                      <Grid container spacing={2} mt={1} px={1}>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          sx={{
                            borderRight: { sm: "1px solid #ccc", xs: "none" },
                            paddingBottom: 1,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            fontWeight="bold"
                            fontSize={16}
                            textAlign="center"
                          >
                            Condição Comercial
                          </Typography>
                          <Typography
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              textAlign: "center",
                            }}
                          >
                            {checklistData.cab.condicao_checklist_respondido}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          sx={{
                            paddingBottom: 1,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            fontWeight="bold"
                            fontSize={16}
                            textAlign="center"
                          >
                            Valor de Venda
                          </Typography>
                          <Typography textAlign="center">
                            {formatCurrency2(
                              checklistData.cab.valor_checklist_respondido.toString(),
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            {checklistData.checklist.grupo_perguntas.map((grupo: any) => (
              <>
                <Box key={grupo.id} mb={4} mt={10}>
                  <Grid container spacing={2}>
                    <Grid item={true} xs={12}>
                      <Typography
                        variant="h5"
                        bgcolor="black"
                        color="white"
                        textAlign="center"
                        height="auto"
                        mt={-4}
                        sx={{
                          paddingTop: "10px",
                          fontSize: { xs: "1.2rem", sm: "1.5rem" },
                        }}
                      >
                        {grupo.descricao_checklist_grupo}
                      </Typography>
                      {grupo.ajuda_checklist_grupo && (
                        <Box
                          mb={0}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          p={1}
                          border={2}
                        >
                          <Typography
                            variant="h4"
                            sx={{ flex: 1, fontWeight: "bold" }}
                            textAlign="center"
                          >
                            {grupo.ajuda_checklist_grupo}
                          </Typography>
                        </Box>
                      )}
                      {grupo.imagem_checklist_grupo && (
                        <Box
                          mb={0}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          p={1}
                          border={2}
                        >
                          <img
                            src={`${process.env.REACT_APP_API_DOMAIN}/storage/${grupo.imagem_checklist_grupo}`}
                            alt={`Imagem do grupo: ${grupo.descricao_checklist_grupo}`}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "300px",
                              margin: "10px auto",
                              display: "block",
                            }}
                          />
                        </Box>
                      )}

                      {grupo.perguntas.map((pergunta: any) => {
                        const respostasEnviadas =
                          checklistData?.resposta?.filter(
                            (r) => r.id_checklist_pergunta === pergunta.id,
                          ) || [];

                        const temFotos = respostasEnviadas.some(
                          (r) => r.fotos && r.fotos.length > 0,
                        );

                        return (
                          <Box
                            key={pergunta.id}
                            mb={0}
                            display="flex"
                            flexDirection={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            p={1}
                            border={2}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              sx={{ flex: 2 }}
                            >
                              {temFotos && (
                                <IconButton
                                  onClick={() => {
                                    const perguntaComFoto =
                                      respostasEnviadas.find(
                                        (r) => r.fotos?.length,
                                      );
                                    if (perguntaComFoto) {
                                      setSelectedResponsePhoto(
                                        pergunta.id.toString(),
                                      );
                                    }
                                  }}
                                  sx={{ mr: 1, color: "primary.main" }}
                                >
                                  <CameraAlt />
                                </IconButton>
                              )}
                              <Typography
                                variant="h6"
                                sx={{
                                  marginRight: { xs: 0, sm: "-16px" },
                                  fontSize: { xs: 14, sm: 16 },
                                  mb: { xs: 1, sm: 0 },
                                }}
                              >
                                {pergunta.descricao_checklist_pergunta}
                                {pergunta.obrigatorio_checklist_pergunta ===
                                  true && (
                                  <Typography
                                    component="span"
                                    color="red"
                                    ml={1}
                                    fontSize={23}
                                  >
                                    *
                                  </Typography>
                                )}
                              </Typography>
                            </Box>

                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent={{
                                xs: "center",
                                sm: "flex-end",
                              }}
                              sx={{
                                flex: 2,
                                width: { xs: "100%", sm: "auto" },
                              }}
                            >
                              {pergunta.respostas.map((resposta: any) => (
                                <React.Fragment key={resposta.id}>
                                  {resposta.valor_checklist_respostas === "S" ||
                                  resposta.valor_checklist_respostas === "N" ||
                                  resposta.valor_checklist_respostas ===
                                    "NA" ? (
                                    <IconButton
                                      key={resposta.id}
                                      sx={{
                                        color:
                                          selectedIcons[pergunta.id] ===
                                          resposta.valor_checklist_respostas
                                            ? resposta.valor_checklist_respostas ===
                                              "S"
                                              ? "green"
                                              : resposta.valor_checklist_respostas ===
                                                  "N"
                                                ? "red"
                                                : resposta.valor_checklist_respostas ===
                                                    "NA"
                                                  ? "orange"
                                                  : "blue"
                                            : "rgba(0, 0, 0, 0.38)",
                                        fontSize: { xs: 30, sm: 40 },
                                      }}
                                      aria-label={
                                        resposta.descricao_checklist_resposta
                                      }
                                      disabled={
                                        selectedIcons[pergunta.id] !==
                                        resposta.valor_checklist_respostas
                                      }
                                    >
                                      {resposta.valor_checklist_respostas ===
                                      "S" ? (
                                        <RadioButtonUnchecked
                                          sx={{ fontSize: { xs: 30, sm: 40 } }}
                                        />
                                      ) : resposta.valor_checklist_respostas ===
                                        "N" ? (
                                        <Close
                                          sx={{ fontSize: { xs: 30, sm: 40 } }}
                                        />
                                      ) : resposta.valor_checklist_respostas ===
                                        "NA" ? (
                                        // <Typography
                                        //   sx={{ fontSize: { xs: 16, sm: 16 } }}
                                        // >
                                        //   NA
                                        // </Typography>
                                        <ChangeHistory
                                          sx={{ fontSize: { xs: 30, sm: 40 } }}
                                        />
                                      ) : (
                                        ""
                                      )}
                                    </IconButton>
                                  ) : selectedDescriptions[pergunta.id] ? (
                                    <Box
                                      sx={{
                                        ml: { xs: 1, sm: 2 },
                                        p: { xs: 1, sm: 2 },
                                        backgroundColor: "#f5f5f5",
                                        borderRadius: 1,
                                        borderLeft: "4px solid #3f51b5",
                                        width: "100%",
                                        maxWidth: { xs: "100%", sm: "400px" },
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                          boxShadow:
                                            "0 4px 8px rgba(0,0,0,0.2)",
                                          transform: "translateY(-2px)",
                                        },
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: "400",
                                          color: "#333",
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                          fontSize: { xs: 14, sm: 16 },
                                        }}
                                      >
                                        {selectedDescriptions[pergunta.id]}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                </React.Fragment>
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                      {grupo.rodape_checklist_grupo && (
                        <Typography
                          variant="h6"
                          bgcolor="black"
                          color="white"
                          textAlign="center"
                          height={50}
                          sx={{ paddingTop: "13px" }}
                        >
                          {grupo.rodape_checklist_grupo}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </>
            ))}
            {renderPhotoGallery()}
            <Box>
              <Typography
                variant="h5"
                align="center"
                gutterBottom
                fontWeight={"bold"}
                mb={3}
              >
                OBSERVAÇÕES
              </Typography>
              {checklistData?.observacoes &&
              checklistData.observacoes.length > 0 ? (
                checklistData.observacoes.map(
                  (observacao: ObservationResponse) => (
                    <Card key={observacao.id} sx={{ marginBottom: 2 }}>
                      <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                        <Box display="flex" alignItems="center">
                          <ErrorOutline
                            sx={{
                              marginRight: 1,
                              fontSize: { xs: 20, sm: 24 },
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ fontSize: { xs: 16, sm: 20 } }}
                          >
                            {`Observação ${observacao.id}`}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          mt={1}
                          sx={{ fontSize: { xs: 14, sm: 16 } }}
                        >
                          {observacao.observacao}
                        </Typography>
                      </CardContent>
                    </Card>
                  ),
                )
              ) : (
                <Typography variant="body1" align="center">
                  Nenhuma observação encontrada.
                </Typography>
              )}
            </Box>
            <AppBar
              position="fixed"
              sx={{
                top: "auto",
                bottom: 0,
                backgroundColor: "white",
                height: {
                  xs:
                    checklistData?.checklist.valida_checklist === false
                      ? 110
                      : 160,
                  sm: 110,
                },
              }}
            >
              <Grid container p={2}>
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  spacing={2}
                >
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        width: {
                          xs: "100%",
                          sm:
                            checklistData?.checklist.valida_checklist === false
                              ? "100%"
                              : "50%",
                        },
                      }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          height: 50,
                          marginTop: { xs: 1, sm: 2 },
                          backgroundColor: "black",
                          borderRadius: 2,
                          alignItems: "center",
                          ":hover": {
                            bgcolor: "#5c5c5c",
                          },
                          fontWeight: "bold",
                        }}
                        fullWidth
                        disabled={loading || pdfGenerated}
                        onClick={handleGeneratePDF}
                      >
                        Gerar PDF
                        <PictureAsPdf sx={{ marginLeft: 1 }} />
                      </Button>
                    </Box>

                    {checklistData?.checklist.valida_checklist && (
                      <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
                        <Button
                          variant="contained"
                          sx={{
                            height: 50,
                            marginTop: { xs: 0, sm: 2 },
                            backgroundColor: checklistData?.checklist.validado
                              ? "#E8F5E9"
                              : "#4CAF50",
                            color: checklistData?.checklist.validado
                              ? "#2E7D32"
                              : "#FFF",
                            borderRadius: 2,
                            alignItems: "center",
                            fontWeight: "bold",
                            textTransform: "none",
                            fontSize: "1rem",
                            boxShadow: checklistData?.checklist.validado
                              ? "0 2px 4px rgba(0,0,0,0.1)"
                              : "0 2px 6px rgba(0,0,0,0.2)",
                            border: checklistData?.checklist.validado
                              ? "1px solid #C8E6C9"
                              : "none",
                            ":hover": {
                              bgcolor: checklistData?.checklist.validado
                                ? "#E8F5E9"
                                : "#388E3C",
                              boxShadow: checklistData?.checklist.validado
                                ? "0 2px 4px rgba(0,0,0,0.1)"
                                : "0 4px 8px rgba(0,0,0,0.3)",
                            },
                            ":disabled": {
                              backgroundColor: "#E8F5E9",
                              color: "#2E7D32",
                            },
                            transition: "all 0.3s ease",
                          }}
                          fullWidth
                          disabled={
                            checklistData?.checklist.validado || loading
                          }
                          onClick={handleValidateChecklist}
                        >
                          {checklistData?.checklist.validado ? (
                            <Box display="flex" alignItems="center">
                              <DoneAll
                                sx={{
                                  marginRight: 1,
                                  color: "#2E7D32",
                                  fontSize: "1.5rem",
                                }}
                              />
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: "bold" }}
                              >
                                {checklistData?.checklist
                                  .checklist_valida_comercial === true
                                  ? "Checklist Validado"
                                  : "Checklist Aprovado"}
                              </Typography>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center">
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: "bold" }}
                              >
                                {checklistData?.checklist
                                  .checklist_valida_comercial === true
                                  ? "Validar Checklist"
                                  : "Aprovar Checklist"}
                              </Typography>
                              <Done sx={{ marginLeft: 1 }} />
                            </Box>
                          )}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </AppBar>
            <Modal
              aria-labelledby="validation-modal-title"
              aria-describedby="validation-modal-description"
              open={openValidationModal}
              onClose={() => setOpenValidationModal(false)}
              closeAfterTransition
            >
              <Fade in={openValidationModal}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "90%", sm: 500 },
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h2"
                    mb={3}
                    textAlign="center"
                  >
                    Deseja validar a compra?
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => setShowValidationFields(true)}
                      disabled={showValidationFields}
                    >
                      Validar
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleConfirmValidation(false)}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : "Não validar"}
                    </Button>
                  </Box>

                  {showValidationFields && (
                    <Box sx={{ mt: 3 }}>
                      <TextField
                        label="Condição Comercial"
                        fullWidth
                        value={commercialCondition}
                        onChange={(e) => setCommercialCondition(e.target.value)}
                        sx={{ mb: 2 }}
                        multiline
                        minRows={6}
                      />
                      <TextField
                        label="Informe o valor da compra"
                        fullWidth
                        type="text"
                        value={purchaseValue}
                        onChange={handleChange}
                        sx={{ mb: 3 }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => handleConfirmValidation(true)}
                        disabled={
                          loading || !commercialCondition || !purchaseValue
                        }
                      >
                        {loading ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Confirmar Validação"
                        )}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Fade>
            </Modal>
            <Modal
              open={!!selectedResponsePhoto}
              onClose={() => setSelectedResponsePhoto(null)}
              aria-labelledby="response-photo-modal"
              aria-describedby="response-photo-modal-description"
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "90%",
                  maxWidth: "800px",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  p: 1,
                  outline: "none",
                }}
              >
                {selectedResponsePhoto && (
                  <>
                    <Typography variant="h6" gutterBottom textAlign="center">
                      Fotos da Resposta
                    </Typography>
                    <Grid container spacing={2}>
                      {getResponsePhotos(Number(selectedResponsePhoto)).map(
                        (foto) => (
                          <Grid item xs={12} sm={6} md={4} key={foto.id}>
                            <Card>
                              <CardContent sx={{ p: 0 }}>
                                <img
                                  src={`${process.env.REACT_APP_API_DOMAIN}/storage/${foto.foto_checklist_respondido_foto}`}
                                  alt={`Foto da resposta ${foto.id}`}
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: "300px",
                                    objectFit: "contain",
                                  }}
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                        ),
                      )}
                    </Grid>
                  </>
                )}
                <Box textAlign="center" mt={2}>
                  <Button
                    variant="contained"
                    onClick={() => setSelectedResponsePhoto(null)}
                    sx={{ backgroundColor: "black" }}
                  >
                    Fechar
                  </Button>
                </Box>
              </Box>
            </Modal>
            <Modal
              open={!!selectedResponsePhoto}
              onClose={() => setSelectedResponsePhoto(null)}
              aria-labelledby="response-photo-modal"
              aria-describedby="response-photo-modal-description"
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "95%",
                  maxWidth: "800px",
                  maxHeight: "90vh",
                  overflowY: "auto",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  p: 2,
                  outline: "none",
                  borderRadius: 2,
                  "@media (max-width: 600px)": {
                    width: "95%",
                    p: 1,
                  },
                }}
              >
                {selectedResponsePhoto && (
                  <>
                    <Typography
                      variant="h6"
                      gutterBottom
                      textAlign="center"
                      sx={{ fontSize: { xs: 16, sm: 20 } }}
                    >
                      Fotos da Resposta
                    </Typography>
                    <Grid container spacing={2}>
                      {getResponsePhotos(Number(selectedResponsePhoto)).map(
                        (foto) => (
                          <Grid item xs={12} sm={6} md={4} key={foto.id}>
                            <Card>
                              <CardContent sx={{ p: 0 }}>
                                <img
                                  src={`${process.env.REACT_APP_API_DOMAIN}/storage/${foto.foto_checklist_respondido_foto}`}
                                  alt={`Foto da resposta ${foto.id}`}
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: "300px",
                                    objectFit: "contain",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    setFullScreenImage(
                                      `${process.env.REACT_APP_API_DOMAIN}/storage/${foto.foto_checklist_respondido_foto}`,
                                    )
                                  }
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                        ),
                      )}
                    </Grid>
                  </>
                )}
                <Box textAlign="center" mt={2}>
                  <Button
                    variant="contained"
                    onClick={() => setSelectedResponsePhoto(null)}
                    sx={{ backgroundColor: "black" }}
                  >
                    Fechar
                  </Button>
                </Box>
              </Box>
            </Modal>
            <Modal
              open={!!fullScreenImage}
              onClose={() => setFullScreenImage(null)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0, 0, 0, 0.9)",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100vw",
                  height: "100vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  onClick={() => setFullScreenImage(null)}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    color: "white",
                    zIndex: 10,
                    bgcolor: "rgb(255, 0, 0)",
                  }}
                >
                  <Close />
                </IconButton>

                <img
                  src={fullScreenImage ?? ""}
                  alt="Imagem em tela cheia"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Modal>

            <Modal
              aria-labelledby="transition-modal-title"
              aria-describedby="transition-modal-description"
              open={openModal}
              closeAfterTransition
            >
              <Box>
                <Fade in={openModal}>
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: 2,
                      boxShadow: 24,
                      p: 4,
                      maxWidth: "90vw",
                      margin: 3,
                      height: "auto",
                      marginTop: "20vh",
                      textAlign: "center",
                    }}
                  >
                    <Grid container spacing={2} p={2} mt={-6}>
                      <Grid item xs={12}>
                        {loading && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mt: 2,
                            }}
                          >
                            <Typography variant="h6">
                              Aguarde seu PDF está sendo gerado!
                            </Typography>
                          </Box>
                        )}
                        {loading && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mt: 3,
                            }}
                          >
                            <CircularProgress sx={{ color: "black" }} />
                          </Box>
                        )}
                      </Grid>
                      {pdfUrl && (
                        <>
                          <Button
                            onClick={() => {
                              setOpenModal(false);
                              setPdfGenerated(false);
                              setPdfUrl("");
                            }}
                            sx={{
                              backgroundColor: "red",
                              color: "white",
                              ":hover": {
                                bgcolor: "#5c5c5c",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                typography: {
                                  sm: "h6",
                                  xs: "caption",
                                  lg: "h6",
                                  md: "h6",
                                },
                              }}
                            >
                              Fechar
                            </Typography>
                          </Button>
                          <Grid
                            item
                            xs={12}
                            sx={{ height: { xs: 250, sm: 350, lg: 500 } }}
                          >
                            <iframe
                              src={`${pdfUrl}`}
                              title="PDF"
                              width="100%"
                              height="100%"
                            />
                            <Typography
                              sx={{
                                typography: {
                                  sm: "h6",
                                  xs: "caption",
                                  lg: "h6",
                                  md: "h6",
                                },
                              }}
                            >
                              PDF Gerado com sucesso!
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                </Fade>
              </Box>
            </Modal>
          </>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default VisualizarChecklist;
